import React, { useEffect, useRef, useState } from 'react';
import { money, reduceMotion } from './Ring';

/* Bars grow from their last width (kept across tab switches), never from zero. */
const prevW = {};
const prevCount = {};
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const listOr = (xs) => xs.join(', ').replace(/, ([^,]*)$/, ' or $1');

function Bar({ k, widths }) {
  const [w, setW] = useState(() => prevW[k] || [0, 0, 0, 0]);
  useEffect(() => {
    prevW[k] = widths;
    if (reduceMotion()) { setW(widths); return undefined; }
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setW(widths)));
    return () => cancelAnimationFrame(id);
  }, [k, widths.join()]); // eslint-disable-line
  return (
    <div className="gph-bar" aria-hidden="true">
      <i className="ok" style={{ width: `${w[0]}%` }} /><i className="near" style={{ width: `${w[1]}%` }} /><i className="over" style={{ width: `${w[2]}%` }} /><i className="done" style={{ width: `${w[3]}%` }} />
    </div>
  );
}

function Row({ r, max, lit, onStage }) {
  const done = r.key === 'settled';
  const widths = done ? [0, 0, 0, (r.count / max) * 100] : [(r.ok / max) * 100, (r.near / max) * 100, (r.over / max) * 100, 0];
  const label = done
    ? `Settled: ${plural(r.count, 'file')}, ${money(r.value)} settled this month`
    : `${r.label}: ${plural(r.count, 'file')}, ${money(r.value)}, ${r.ok} on track, ${r.near} near limit, ${r.over} over limit`;
  return (
    <button type="button" className={`gph-row${lit ? ' hlit' : ''}`} onClick={() => onStage?.(r.key)} aria-label={label} data-testid={`gph-row-${r.key}`}>
      <div className="gph-name"><b>{r.label}</b><small className="num">{done ? `${r.count} this month` : `${plural(r.count, 'file')} · ${money(r.value)}`}</small></div>
      <Bar k={r.key} widths={widths} />
      <div className="gph-stat">
        {done ? (
          <><b className="done">{plural(r.count, 'file')}</b><small>{money(r.value)} settled</small></>
        ) : (
          <>
            {r.over ? <b className="over">{r.over} over{r.near ? ` · ${r.near} near` : ''}</b> : r.near ? <b className="near">{r.near} near limit</b> : <b className="ok">All on track</b>}
            <small className="num">Oldest {plural(r.oldest, 'day')}{r.limit != null ? ` · limit ${r.limit}` : ''}</small>
          </>
        )}
      </div>
    </button>
  );
}

/* Pipeline health: totals by risk, one bar per stage with files, Settled last. */
export default function PipelineHealth({ live, onStage }) {
  const ph = live?.pipeline_health;
  const [lit, setLit] = useState({});
  const first = useRef(true);
  const stages = ph?.stages || [];
  const settled = ph?.settled;

  // A live stage change bumps a row's count — flash that row blue once.
  useEffect(() => {
    if (!ph) return undefined;
    const next = {};
    for (const s of [...stages, settled].filter(Boolean)) {
      if (!first.current && prevCount[s.key] != null && s.count !== prevCount[s.key]) next[s.key] = true;
      prevCount[s.key] = s.count;
    }
    first.current = false;
    if (!Object.keys(next).length) return undefined;
    setLit(next);
    const id = setTimeout(() => setLit({}), 1900);
    return () => clearTimeout(id);
  }, [ph?.stages, ph?.settled]); // eslint-disable-line

  if (!ph) return null;
  const rows = settled?.count ? [...stages, settled] : stages;
  const max = Math.max(1, ...rows.map((r) => r.count));
  const pct = (n) => (ph.count ? `${Math.round((n / ph.count) * 100)}% of files` : '—');
  return (
    <section className="gpj-panel gph" data-testid="gpj-pipeline-health">
      <div className="gpj-panel-h"><h2>Pipeline health</h2><p className="num" data-testid="gph-sub">{plural(ph.count, 'active file')} · {money(ph.value)} in flight</p></div>
      <div className="gph-tot">
        <div className="gph-tile ok" data-testid="gph-tile-ok"><span><i style={{ background: 'var(--green)' }} />On track</span><strong className="num">{ph.ok}</strong><small>{pct(ph.ok)}</small></div>
        <div className="gph-tile near" data-testid="gph-tile-near"><span><i style={{ background: 'var(--amber)' }} />Near limit</span><strong className="num">{ph.near}</strong><small>{pct(ph.near)}</small></div>
        <div className="gph-tile over" data-testid="gph-tile-over"><span><i style={{ background: 'var(--red)' }} />Over limit</span><strong className="num">{ph.over}</strong><small>{ph.over ? 'Act on these first' : 'None overdue'}</small></div>
      </div>
      <div data-testid="gph-rows">
        {rows.length === 0 && <div className="gpj-empty">No files in the pipeline right now.</div>}
        {rows.map((r) => <Row key={r.key} r={r} max={max} lit={!!lit[r.key]} onStage={onStage} />)}
      </div>
      <div className="gph-foot">
        <span data-testid="gph-empty-stages">{ph.empty?.length ? `No files in ${listOr(ph.empty.map((l) => (l === 'Fact Find' ? l : l.toLowerCase())))}.` : 'Every stage has files.'}</span>
        <span className="lg"><span><i style={{ background: 'var(--green)' }} />On track</span><span><i style={{ background: 'var(--amber)' }} />Near limit</span><span><i style={{ background: 'var(--red)' }} />Over limit</span></span>
      </div>
    </section>
  );
}
